import React, { createContext, useContext, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cn } from '../utils/cn.js';

/**
 * Sidebar Context for managing sidebar state across components
 */
const SidebarContext = createContext({
  isDesktopOpen: true,
  isMobileOpen: false,
  toggleDesktop: () => {},
  toggleMobile: () => {},
  closeMobile: () => {},
});

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

/**
 * SidebarProvider - Manages sidebar state
 */
export function SidebarProvider({ children, defaultDesktopOpen = true }) {
  const [isDesktopOpen, setIsDesktopOpen] = useState(defaultDesktopOpen);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const value = {
    isDesktopOpen,
    isMobileOpen,
    toggleDesktop: () => setIsDesktopOpen(!isDesktopOpen),
    toggleMobile: () => setIsMobileOpen(!isMobileOpen),
    closeMobile: () => setIsMobileOpen(false),
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * Sidebar - Main container
 */
export const Sidebar = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <aside
      ref={ref}
      className={cn(
        'flex h-screen w-80 flex-col bg-white text-gray-900',
        'border-r border-gray-200',
        className
      )}
      {...props}
    >
      {children}
    </aside>
  )
);
Sidebar.displayName = 'Sidebar';

/**
 * SidebarHeader - Header section of sidebar
 */
export const SidebarHeader = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col gap-2 border-b border-gray-200 px-4 py-4', className)}
      {...props}
    />
  )
);
SidebarHeader.displayName = 'SidebarHeader';

/**
 * SidebarFooter - Footer section of sidebar
 */
export const SidebarFooter = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col gap-2 border-t border-gray-200 px-4 py-4', className)}
      {...props}
    />
  )
);
SidebarFooter.displayName = 'SidebarFooter';

/**
 * SidebarContent - Scrollable content area
 */
export const SidebarContent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex-1 overflow-y-auto px-4 py-6', className)}
      {...props}
    />
  )
);
SidebarContent.displayName = 'SidebarContent';

/**
 * SidebarMenu - Menu container (typically ul)
 */
export const SidebarMenu = React.forwardRef(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn('flex w-full flex-col gap-1', className)}
      {...props}
    />
  )
);
SidebarMenu.displayName = 'SidebarMenu';

/**
 * SidebarMenuButton - Individual menu item button
 */
export const SidebarMenuButton = React.forwardRef(
  ({ className, isActive = false, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex h-9 w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isActive && 'bg-blue-50 text-blue-600 border-r-2 border-blue-600',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

/**
 * SidebarMenuItem - Menu item wrapper (li element)
 */
export const SidebarMenuItem = React.forwardRef(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('group/menu-item', className)} {...props} />
  )
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

/**
 * SidebarMenuSub - Submenu container
 */
export const SidebarMenuSub = React.forwardRef(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn('ml-6 flex w-full flex-col gap-1', className)}
      {...props}
    />
  )
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

/**
 * SidebarMenuSubButton - Submenu item button
 */
export const SidebarMenuSubButton = React.forwardRef(
  ({ className, isActive = false, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex h-9 w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors',
        'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isActive && 'bg-blue-50 text-blue-600 border-r-2 border-blue-600',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

/**
 * SidebarMenuSubItem - Submenu item wrapper (li element)
 */
export const SidebarMenuSubItem = React.forwardRef(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('group/menu-sub-item', className)} {...props} />
  )
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

/**
 * SidebarMenuSection - Grouped section of menu items with collapsible header
 */
export function SidebarMenuSection({ 
  title, 
  icon: Icon,
  children, 
  defaultOpen = true,
  className 
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <Collapsible.Trigger className={cn(
        'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider',
        'text-gray-500 hover:text-gray-700',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        className
      )}>
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
          {title}
        </span>
        <svg
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 transition-transform',
            !isOpen && 'rotate-90'
          )}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </Collapsible.Trigger>

      <Collapsible.Content className="overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <SidebarMenu className="mt-2 space-y-1">
          {children}
        </SidebarMenu>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/**
 * SidebarTrigger - Button to toggle sidebar visibility
 */
export const SidebarTrigger = React.forwardRef(
  ({ className, onClick, ...props }, ref) => {
    const { toggleMobile, toggleDesktop } = useSidebar();
    
    const handleClick = (e) => {
      toggleMobile();
      toggleDesktop();
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        onClick={handleClick}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          className
        )}
        {...props}
      >
        <svg
          className="h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      </button>
    );
  }
);
SidebarTrigger.displayName = 'SidebarTrigger';

/**
 * SidebarRail - Collapsible mini-rail for desktop
 */
export const SidebarRail = React.forwardRef(
  ({ className, ...props }, ref) => (
    <aside
      ref={ref}
      className={cn(
        'hidden flex-col items-center justify-between gap-4 border-r border-gray-200 bg-white px-2 py-4 lg:flex',
        'w-14',
        className
      )}
      {...props}
    />
  )
);
SidebarRail.displayName = 'SidebarRail';

/**
 * SidebarGroup - Grouped section container
 */
export const SidebarGroup = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex w-full flex-col gap-2', className)}
      {...props}
    />
  )
);
SidebarGroup.displayName = 'SidebarGroup';

/**
 * SidebarGroupLabel - Label for a sidebar group
 */
export const SidebarGroupLabel = React.forwardRef(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider',
        'text-gray-500 hover:text-gray-700',
        'transition-colors',
        className
      )}
      {...props}
    />
  )
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

/**
 * SidebarGroupAction - Action button for a sidebar group (e.g., add button)
 */
export const SidebarGroupAction = React.forwardRef(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-gray-100',
        'text-gray-400 hover:text-gray-600',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        'transition-colors',
        className
      )}
      {...props}
    />
  )
);
SidebarGroupAction.displayName = 'SidebarGroupAction';

/**
 * SidebarGroupContent - Content wrapper for a sidebar group
 */
export const SidebarGroupContent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('w-full', className)}
      {...props}
    />
  )
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

/**
 * SidebarMenuAction - Action button for a menu item
 */
export const SidebarMenuAction = React.forwardRef(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md',
        'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        'transition-colors',
        'opacity-0 group-hover/menu-item:opacity-100',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuAction.displayName = 'SidebarMenuAction';

/**
 * SidebarMenuBadge - Badge for a menu item
 */
export const SidebarMenuBadge = React.forwardRef(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center px-2 py-0.5 rounded-md',
        'bg-blue-100 text-blue-600 text-xs font-semibold',
        'ml-auto',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

/**
 * SidebarInset - Main content area wrapper alongside sidebar
 */
export const SidebarInset = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative flex min-h-screen w-full flex-col', className)}
      {...props}
    />
  )
);
SidebarInset.displayName = 'SidebarInset';

